import { GetServerSidePropsContext } from 'next';

import { CompanyAccount } from '@/models/companyModels';

import { appendSetCookie } from './cookies';

export const V3_MIGRATION_COOKIE = 'portal_v3_migration';

const V3_MIGRATION_COOKIE_VALUE = '1';

export const PORTAL_REWARDS_ROUTE = '/app/rewards/';
export const PORTAL_REWARDS_V3_ROUTE = '/app/rewards/v3';
export const STOREFRONT_ROUTE = '/storefront';

/**
 * A company is inside the admin3 migration window when admin3 is switched on but the advocate
 * portal has not been handed over yet.
 *
 * This reads the migration flag itself rather than inferring the window from portal_v3_access.
 * Inferring it would be self-defeating: applyV3MigrationRoute raises portal_v3_access, so a window
 * derived from it stops holding the moment the route takes effect, and a second visit to an entry
 * route would read its own override as "migration finished" and switch the session back to V2.
 */
export function isMidMigration(company?: CompanyAccount | null): boolean {
  return Boolean(company?.admin3_access) && Boolean(company?.admin3_migration_in_progress);
}

export function isV3MigrationRequested(cookieValue?: string): boolean {
  return cookieValue === V3_MIGRATION_COOKIE_VALUE;
}

/**
 * Grants the V3 experience to a session that asked for it through one of the migration routes, and
 * only while the company is still mid-migration. Routing, menu, capabilities and page guards all
 * read portal_v3_access off the company held in the store, so raising it here is what turns the
 * whole portal into its V3 form rather than only one page.
 *
 * Companies outside the migration window are returned untouched. That is what stops the routes from
 * becoming a way to opt out of the real flag, and it lets a cookie left behind by a finished
 * migration lapse on its own.
 */
export function applyV3MigrationRoute(company: CompanyAccount | null, requested: boolean): CompanyAccount | null {
  if (!company || !requested || !isMidMigration(company)) return company;
  return { ...company, portal_v3_access: true, v3_via_migration_route: true };
}

export function setV3MigrationCookie(res: GetServerSidePropsContext['res']) {
  appendSetCookie(res, `${V3_MIGRATION_COOKIE}=${V3_MIGRATION_COOKIE_VALUE}; Path=/; SameSite=Lax`);
}

export function clearV3MigrationCookie(res: GetServerSidePropsContext['res']) {
  appendSetCookie(res, `${V3_MIGRATION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`);
}
